import React, { useState, useCallback } from 'react';
import { BarChart3, PieChart, LineChart } from 'lucide-react';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, 
  ResponsiveContainer, PieChart as RechartsPieChart, Pie, Cell, 
  Area, AreaChart 
} from 'recharts';
import { CHART_COLORS, formatChartValue, createChartTooltip } from './utils/chartHelpers';

const ChartVisualization = React.memo(({ chartRecommendations, originalQuery }) => {
  const [selectedChart, setSelectedChart] = useState(0);

  const renderChart = useCallback((recommendation) => {
    const chartData = recommendation.data;
    const isCurrency = recommendation.is_currency || false;
    
    switch (recommendation.type) {
      case 'bar':
        const BarCustomLabel = (props) => {
          const { x, y, width, value } = props;
          return (
            <text x={x + width / 2} y={y - 5} textAnchor="middle" fontSize="12" fill="#374151" fontWeight="600">
              {formatChartValue(value, isCurrency)}
            </text>
          );
        };

        return (
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={chartData} margin={{ top: 40, right: 30, left: 20, bottom: 80 }}>
              <XAxis 
                dataKey="category" 
                angle={-45} 
                textAnchor="end" 
                height={100} 
                fontSize={11}
                interval={0}
              />
              <YAxis hide />
              <Tooltip content={({ active, payload }) => createChartTooltip(active, payload, isCurrency)} />
              <Legend />
              <Bar dataKey="value" fill={CHART_COLORS[0]} name={recommendation.yAxis} label={<BarCustomLabel />} />
            </BarChart>
          </ResponsiveContainer>
        );

      case 'pie':
        return (
          <ResponsiveContainer width="100%" height={400}>
            <RechartsPieChart>
              <Pie
                data={chartData}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={120}
                label={({name, percent}) => `${name} ${(percent * 100).toFixed(1)}%`}
              >
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip content={({ active, payload }) => createChartTooltip(active, payload, isCurrency)} />
              <Legend />
            </RechartsPieChart>
          </ResponsiveContainer>
        );

      case 'area':
        return (
          <ResponsiveContainer width="100%" height={400}>
            <AreaChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="category" fontSize={11} angle={-45} textAnchor="end" height={80} interval={0} />
              <YAxis tickFormatter={(value) => formatChartValue(value, isCurrency)} />
              <Tooltip content={({ active, payload }) => createChartTooltip(active, payload, isCurrency)} />
              <Legend />
              <Area type="monotone" dataKey="value" stroke={CHART_COLORS[1]} fill={CHART_COLORS[1]} fillOpacity={0.6} name={recommendation.yAxis} />
            </AreaChart>
          </ResponsiveContainer>
        );

      default:
        return <div>Unsupported chart type: {recommendation.type}</div>;
    }
  }, []);

  if (!chartRecommendations || chartRecommendations.length === 0) return null;

  const currentRecommendation = chartRecommendations[selectedChart];

  return (
    <div style={{ marginTop: '1.5rem', width: '1400px', maxWidth: '100%', margin: '0 auto' }}>
      <div style={{ backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
        <div style={{ padding: '1rem', borderBottom: '1px solid #e2e8f0', backgroundColor: '#f8fafc' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h4 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '600' }}>Data Visualization</h4>
            {chartRecommendations.length > 1 && (
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                {chartRecommendations.map((rec, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedChart(index)}
                    style={{
                      padding: '0.5rem 0.75rem',
                      backgroundColor: selectedChart === index ? '#4F7CFF' : '#fff',
                      color: selectedChart === index ? 'white' : '#64748b',
                      border: '1px solid #e2e8f0',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '0.8rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.25rem'
                    }}
                  >
                    {rec.type === 'bar' && <BarChart3 style={{ width: '0.9rem', height: '0.9rem' }} />}
                    {rec.type === 'pie' && <PieChart style={{ width: '0.9rem', height: '0.9rem' }} />}
                    {(rec.type === 'line' || rec.type === 'area') && <LineChart style={{ width: '0.9rem', height: '0.9rem' }} />}
                    {rec.title}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div>
            <h5 style={{ margin: 0, fontSize: '1rem', color: '#374151' }}>{currentRecommendation.title}</h5>
            <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.875rem', color: '#64748b' }}>{currentRecommendation.description}</p>
          </div>
        </div>
        <div style={{ padding: '1rem' }}>
          {renderChart(currentRecommendation)}
        </div>
      </div>
    </div>
  );
});

export default ChartVisualization;